import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import type { HappinessCountsDto, HappinessRatingValue } from '@taqon/contracts';

/**
 * The three faces, shared by the happiness meter (FRD-HRP-084) and feedback on actions
 * (FRD-CORE-062). The label is always shown: colour and icon never stand alone.
 */
export const FACES: readonly {
  value: HappinessRatingValue;
  label: string;
  icon: ReactNode;
  color: 'success' | 'warning' | 'error';
}[] = [
  { value: 'happy', label: 'Happy', icon: <SentimentVerySatisfiedIcon />, color: 'success' },
  { value: 'neutral', label: 'Neutral', icon: <SentimentNeutralIcon />, color: 'warning' },
  { value: 'unhappy', label: 'Unhappy', icon: <SentimentVeryDissatisfiedIcon />, color: 'error' },
];
export const FACE_LABEL: Record<HappinessRatingValue, string> = {
  happy: 'Happy',
  neutral: 'Neutral',
  unhappy: 'Unhappy',
};

export const totalOf = (counts: HappinessCountsDto) =>
  counts.happy + counts.neutral + counts.unhappy;
export const share = (count: number, total: number) =>
  total === 0 ? '—' : `${String(Math.round((count / total) * 100))}%`;

/** A mix of faces as a bar. Decorative: the same figures are in the row's cells. */
export function Mix({ counts }: { counts: HappinessCountsDto }) {
  const total = totalOf(counts);
  return (
    <Box
      aria-hidden
      sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'divider' }}
    >
      {total > 0 &&
        FACES.map((face) => (
          <Box
            key={face.value}
            sx={{
              width: `${String((counts[face.value] / total) * 100)}%`,
              bgcolor: `${face.color}.main`,
            }}
          />
        ))}
    </Box>
  );
}
